import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSpring, config, animated } from 'react-spring';
import { BrowserRouter as Router, Link } from 'react-router-dom';
import Scrollspy from 'react-scrollspy';
import * as cn from 'classnames';
import PropTypes from 'prop-types';
import { capitalize } from 'lodash';

import './NavBar.scss';

const Nav = ({ children, ...props }) => (
  <animated.nav {...props}>
    <div className="container-lg flex-nowrap overflow-auto-x">{children}</div>
  </animated.nav>
);

const NavItems = ({ navItems, onUpdateNav }) => {
  return (
    <Scrollspy
      offset={-5}
      items={navItems}
      className="navbar-nav"
      currentClassName="active"
      onUpdate={onUpdateNav}
    >
      {navItems.map((item, idx) => (
        <li key={idx} className="nav-item">
          <Link className="nav-link" to={`#${item}`}>
            {capitalize(item)}
          </Link>
        </li>
      ))}
    </Scrollspy>
  );
};

const NavBarBrand = ({ navBrand }) => navBrand;

function NavBar({
  className,
  navBrand,
  navItems,
  isMobile,
  isSideBarOpen,
  setIsSideBarOpen,
  ...props
}) {
  const [isNavbarFixed, setNavbarFixed] = useState(false);
  const [isShow, setIsShow] = useState(true);
  const [navLeft, setNavLeft] = useState(0);
  const [navWidth, setNavWidth] = useState(0);

  const handleNavigation = useCallback((e) => {
    const _window = e.currentTarget;
    if (_window.pageYOffset === 0) {
      setIsShow(true);
      setNavbarFixed(false);
    } else {
      setIsShow(false);
      if (_window.pageYOffset >= _window.innerHeight) {
        setIsShow(true);
        setNavbarFixed(true);
      }
    }
  }, []);

  useMemo(() => {
    window.addEventListener('scroll', handleNavigation);

    return () => {
      // return a cleanup function to unregister our function since its gonna run multiple times
      window.removeEventListener('scroll', handleNavigation);
    };
  }, [handleNavigation]);

  const onUpdateNav = () => {
    const activeNav = document.querySelector('.nav-item.active');
    if (!!activeNav) {
      const { left, width } = activeNav.getClientRects()[0];
      setNavLeft(left);
      setNavWidth(width);
    }
  };

  useEffect(() => {
    const nb = document.querySelector('.navbar-brand');
    if (!!nb) {
      const { left, width } = nb.getClientRects()[0];
      setNavLeft(left - 8);
      setNavWidth(width + 16);
    }
  }, []);

  const slideNavStyles = useSpring({
    config: { ...config.stiff },
    from: { y: -57 },
    to: { y: isShow || isNavbarFixed ? 0 : -57 },
  });

  let options = {
    ...props,
    className: cn('navbar navbar-expand-lg navbar-dark', {
      'position-fixed shadow-sm': isNavbarFixed,
      [className]: !!className,
    }),
    style: {
      '--active-nav-left': `${navLeft}px`,
      '--active-nav-width': `${navWidth}px`,
      ...slideNavStyles,
    },
  };

  if (isNavbarFixed) {
    options = {
      ...options,
      style: {
        ...options.style,
        background: 'var(--dp-dark-1)',
      },
    };
  }

  return (
    <>
      <Router forceRefresh={true}>
        <Nav {...options}>
          <NavBarBrand navBrand={navBrand} />
          {isMobile && (
            <span
              className="navbar-toggler-icon"
              onClick={() => setIsSideBarOpen(!isSideBarOpen)}
            ></span>
          )}
          {!isMobile && (
            <NavItems navItems={navItems} onUpdateNav={onUpdateNav} />
          )}
        </Nav>
      </Router>
      {isNavbarFixed && (
        <div className="mb-auto" style={{ height: '56px' }}></div>
      )}
    </>
  );
}

NavBar.defaultProps = {
  className: '',
  navBrand: <></>,
  navItems: [],
  isMobile: false,
};

NavBar.propTypes = {
  className: PropTypes.string,
  navBrand: PropTypes.node,
  navItems: PropTypes.array,
  isMobile: PropTypes.bool,
};

export default React.memo(NavBar);
